import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react'
import { Session, User } from '@supabase/supabase-js'
import supabase from '../utils/supabase'
import { ProfileService } from '../entities/Profile'
import { socketService } from '../services/socket.service'

interface AuthContextType {
  session: Session | null
  user: User | null
  loading: boolean
  role: string | null
  centerId: string | null
  isApproved: boolean
  isBlocked: boolean
  accountStatusNotice: 'pending_revoked' | null
  clearAccountStatusNotice: () => void
  signOut: () => Promise<void>
}

// Helper function to decode JWT token
const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error decoding JWT:', error)
    return null
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  useEffect(() => {
    console.log('[AuthContext] AuthProvider MOUNTED');
    return () => console.log('[AuthContext] AuthProvider UNMOUNTED');
  }, []);

  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [role, setRole] = useState<string | null>(null)
  const [centerId, setCenterId] = useState<string | null>(null)
  const [isApproved, setIsApproved] = useState<boolean>(false) // Default to false (fail closed)
  const [isBlocked, setIsBlocked] = useState<boolean>(false)
  const [accountStatusNotice, setAccountStatusNotice] = useState<'pending_revoked' | null>(null)
  const [isProfileChecked, setIsProfileChecked] = useState<boolean>(false) // Track if profile has been checked against DB
  const profileService = new ProfileService()
  const prevIsApprovedRef = React.useRef<boolean | null>(null)
  // Track profile creation in progress to prevent duplicates
  const profileCreationInProgress = React.useRef<Set<string>>(new Set())
  // Track if we've set a maximum loading timeout to prevent infinite loading
  const maxLoadingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  // Ref to track last token value to prevent unnecessary recalculations
  const lastTokenRef = React.useRef<string | null>(null)
  const [tokenVersion, setTokenVersion] = useState(0)

  // Refs to track state for event listeners to avoid stale closures
  const userIdRef = React.useRef<string | null>(null)

  // When true, loading must wait for profile check before stopping (only for fresh sign-ins)
  const requireProfileBeforeLoadRef = React.useRef(false)

  useEffect(() => {
    userIdRef.current = user?.id ?? null
  }, [user?.id])

  // Initialize token ref on mount
  React.useEffect(() => {
    const initialToken = localStorage.getItem('backend_access_token') || ''
    lastTokenRef.current = initialToken.replace(/^"+|"+$/g, '').trim() || null
  }, [])

  // Memoize the backend auth extraction - recalculates when tokenVersion changes
  const backendAuthData = useMemo(() => {
    try {
      const raw = localStorage.getItem('backend_access_token') || ''
      const token = raw.replace(/^"+|"+$/g, '').trim()

      // Only log if token actually changed
      const shouldLog = lastTokenRef.current !== token
      if (shouldLog) {
        lastTokenRef.current = token
      }

      if (!token) {
        if (shouldLog) {
          console.warn('[AuthContext] No backend token found in localStorage');
        }
        return { role: null, centerId: null, isApproved: true }
      }

      if (token.split('.').length !== 3) {
        if (shouldLog) {
          console.warn('[AuthContext] Invalid token format (not a JWT):', token.substring(0, 50) + '...');
        }
        return { role: null, centerId: null, isApproved: true }
      }

      const decoded = decodeJWT(token)
      if (decoded) {
        // Handle potential JSON-encoded strings or string "null" values
        let extractedRole = decoded.role
        let extractedCenterId = decoded.center_id
        // Handle is_approved - default to false if not present in token but token is valid jwt
        // Wait, for backward compatibility during migration, existing tokens won't have it.
        // If we strictly enforce false, current users might get locked out until re-login.
        // But re-login is required to get new token anyway.
        // Let's check if property exists.


        // If role is a JSON string, parse it
        if (typeof extractedRole === 'string' && extractedRole.startsWith('"') && extractedRole.endsWith('"')) {
          try {
            extractedRole = JSON.parse(extractedRole)
          } catch (e) {
            // If parsing fails, use as-is
          }
        }

        // If center_id is the string "null" or null, convert to actual null
        if (extractedCenterId === 'null' || extractedCenterId === null || extractedCenterId === undefined) {
          extractedCenterId = null
        } else if (typeof extractedCenterId === 'string' && extractedCenterId.startsWith('"') && extractedCenterId.endsWith('"')) {
          try {
            extractedCenterId = JSON.parse(extractedCenterId)
            // If parsed result is "null" string, convert to null
            if (extractedCenterId === 'null') {
              extractedCenterId = null
            }
          } catch (e) {
            // If parsing fails, use as-is
          }
        }

        // Only log when token actually changes
        if (shouldLog) {
          console.log('[AuthContext] Token decoded successfully.', {
            role: extractedRole,
            centerId: extractedCenterId,
            roleType: typeof extractedRole,
            centerIdType: typeof extractedCenterId
          });
        }

        return { role: extractedRole || null, centerId: extractedCenterId || null }
      } else {
        if (shouldLog) {
          console.warn('[AuthContext] Failed to decode token');
        }
        return { role: null, centerId: null }
      }
    } catch (error) {
      console.error('[AuthContext] Error extracting backend auth:', error)
      return { role: null, centerId: null }
    }
  }, [tokenVersion]) // Recalculate when tokenVersion changes

  // Update role and centerId state when backendAuthData changes
  // Use functional updates to avoid dependency on current state values
  useEffect(() => {
    setRole((currentRole) => {
      if (currentRole !== backendAuthData.role) {
        return backendAuthData.role
      }
      return currentRole
    })

    setCenterId((currentCenterId) => {
      if (currentCenterId !== backendAuthData.centerId) {
        return backendAuthData.centerId
      }
      return currentCenterId
    })
  }, [backendAuthData.role, backendAuthData.centerId])

  const clearAccountStatusNotice = useCallback(() => setAccountStatusNotice(null), [])

  const refreshAccessState = useCallback(async (userId: string) => {
    try {
      const svc = new ProfileService()
      const [authRes, profile] = await Promise.all([
        supabase.auth.getUser(),
        svc.getByUserId(userId),
      ])
      const authUser = authRes.data?.user as any
      if (!authUser || authUser.id !== userId) return

      const meta = authUser.user_metadata || {}
      const bannedUntil = authUser.banned_until
      const banActive =
        bannedUntil &&
        !Number.isNaN(new Date(bannedUntil).getTime()) &&
        new Date(bannedUntil).getTime() > Date.now()
      setIsBlocked(meta.is_blocked === true || banActive)

      if (profile) {
        const nextApproved = profile.is_approved === true
        if (prevIsApprovedRef.current === true && nextApproved === false) {
          setAccountStatusNotice('pending_revoked')
        }
        prevIsApprovedRef.current = nextApproved
        setIsApproved(nextApproved)
      }
    } catch (e) {
      console.error('[AuthContext] refreshAccessState failed', e)
    }
  }, [])

  // Function to automatically create profile for new users
  const ensureUserProfile = async (user: User | null, retryCount = 0) => {
    if (!user?.id) return

    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000;

    // Prevent duplicate profile creation if already in progress
    if (profileCreationInProgress.current.has(user.id)) {
      console.log('[AuthContext] Profile creation already in progress for user:', user.id)
      return
    }

    try {
      // Check if profile already exists - use a more robust check
      const existingProfile = await profileService.getByUserId(user.id)

      if (!existingProfile) {
        // Mark as in progress to prevent duplicate creation
        profileCreationInProgress.current.add(user.id)

        try {
          // Double-check after marking as in progress (race condition protection)
          const doubleCheckProfile = await profileService.getByUserId(user.id)
          if (doubleCheckProfile) {
            console.log('[AuthContext] Profile was created by another process, skipping creation for user:', user.id)
            profileCreationInProgress.current.delete(user.id)
            await refreshAccessState(user.id)
            setIsProfileChecked(true)
            return
          }

          // Create profile for new user with default role 'representative' (user role no longer exists)
          console.log('[AuthContext] Creating profile for new user:', user.id)
          const phone = user.user_metadata?.phone || user.phone;
          const newProfile = await profileService.createForUser(
            user.id,
            'representative',
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            phone
          )

          if (newProfile) {
            console.log('[AuthContext] Profile created successfully:', newProfile)
            // Also add phone to UserPhones table if provided
            if (phone && newProfile.id) {
              try {
                await profileService.addUserPhone(newProfile.id, phone)
                console.log('[AuthContext] Phone added to UserPhones:', phone)
              } catch (phoneError) {
                console.error('[AuthContext] Failed to add phone to UserPhones:', phoneError)
              }
            }
            await refreshAccessState(user.id)
            setIsProfileChecked(true)
          } else {
            console.warn('[AuthContext] Failed to create profile for user:', user.id)
          }
        } finally {
          // Always remove from in-progress set
          profileCreationInProgress.current.delete(user.id)
        }
      } else {
        console.log('[AuthContext] Profile already exists for user:', user.id)

        // Check if profile role differs from JWT role
        // Get current role from state using a function to ensure we have the latest value
        const jwtRole = role // This will be updated by extractBackendAuth which is called before this
        const currentProfileRole = existingProfile.role

        if (jwtRole && currentProfileRole && jwtRole !== currentProfileRole) {
          console.warn(`[AuthContext] Role mismatch detected! JWT role: ${jwtRole}, Profile role: ${currentProfileRole}. Using profile role.`)
          // Use profile role as source of truth for UI/access control
          setRole(currentProfileRole)
          setCenterId(existingProfile.center_id || null)
          // Don't clear the token immediately - it might still work for some API calls
          // Instead, show a warning and let the user re-login when convenient
          // The old token will be replaced on next login
          console.warn('[AuthContext] Your role has been updated. Please re-login to refresh your authentication token for API access.')
        } else if (currentProfileRole) {
          // If no mismatch, ensure role is set from profile if JWT doesn't have it
          if (!jwtRole && currentProfileRole) {
            setRole(currentProfileRole)
            setCenterId(existingProfile.center_id || null)
          }
        }

        setIsProfileChecked(true)
        await refreshAccessState(user.id)
      }
    } catch (error) {
      console.error(`[AuthContext] Error ensuring user profile (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, error)

      if (retryCount < MAX_RETRIES) {
        console.log(`[AuthContext] Retrying profile fetch in ${RETRY_DELAY}ms...`)
        setTimeout(() => {
          ensureUserProfile(user, retryCount + 1)
        }, RETRY_DELAY)
      } else {
        console.error('[AuthContext] Max retries reached for profile fetch. User might be stuck in loading state.')
      }
    }
  }


  // Effect to handle initial session and auth state changes
  useEffect(() => {
    // Set a maximum timeout to prevent infinite loading (10 seconds)
    maxLoadingTimeoutRef.current = setTimeout(() => {
      console.warn('[AuthContext] Maximum loading timeout reached, forcing loading to false')
      setLoading(false)
    }, 10000)

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting session:', error.message)
        setSession(null)
        setUser(null)
        setRole(null)
        setCenterId(null)
        setIsApproved(false)
        setIsBlocked(false)
        setAccountStatusNotice(null)
        prevIsApprovedRef.current = null
        setIsProfileChecked(false)
        setSessionChecked(true) // Mark as checked
      } else {
        setSession(session)
        setUser(session?.user ?? null)
        userIdRef.current = session?.user?.id ?? null
        setSessionChecked(true) // Mark as checked

        // Ensure profile exists for existing user and check role mismatch
        if (session?.user) {
          // Restored sessions should also wait for profile/approval check completion
          // to avoid flashing PendingAccess for already-approved users.
          requireProfileBeforeLoadRef.current = true
          setIsProfileChecked(false)
          ensureUserProfile(session.user).catch((error) => {
            console.error('[AuthContext] Error ensuring profile:', error)
          })
        } else {
          setIsApproved(false)
          setIsBlocked(false)
          setAccountStatusNotice(null)
          prevIsApprovedRef.current = null
          setIsProfileChecked(false)
        }
      }
    }).catch((error) => {
      console.error('Failed to get session:', error)
      setSession(null)
      setUser(null)
      setRole(null)
      setCenterId(null)
      setIsApproved(false)
      setIsBlocked(false)
      setAccountStatusNotice(null)
      prevIsApprovedRef.current = null
      setIsProfileChecked(false)
      setSessionChecked(true) // Mark as checked even on error
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(`[AuthContext] Auth State Change: ${event}`, { userId: session?.user?.id });
      const nextUser = session?.user ?? null
      const previousUserId = userIdRef.current

      setSession(session)

      setUser(nextUser)
      userIdRef.current = nextUser?.id ?? null

      if (!nextUser) {
        setRole(null)
        setCenterId(null)
        setIsApproved(false)
        setIsBlocked(false)
        setAccountStatusNotice(null)
        prevIsApprovedRef.current = null
        setIsProfileChecked(false)
      }

      // Handle password recovery
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[AuthContext] Password recovery detected via event. Dispatching redirect.');
        window.dispatchEvent(new Event('auth-password-recovery'));
      }

      // Handle token refresh (skip if token is empty to avoid 400)
      if (event === 'TOKEN_REFRESHED' && session?.access_token?.trim()) {
        console.log('[AuthContext] Supabase token refreshed. Refreshing backend token...');
        try {
          const w: any = window as any
          const baseUrl = w._BACKEND_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'

          const res = await fetch(`${baseUrl}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: session.access_token }),
            credentials: 'include',
          })

          if (res.ok) {
            const data = await res.json()
            if (data.access_token) {
              localStorage.setItem('backend_access_token', data.access_token)

              // Helper to trigger events (similar to AuthComponent)
              const triggerStorageEvents = () => {
                window.dispatchEvent(new Event('storage'))
                window.dispatchEvent(new CustomEvent('backend-token-updated', {
                  detail: { role: data.user?.role, center_id: data.user?.center_id }
                }))
              }
              triggerStorageEvents();

              console.log('[AuthContext] Backend token refreshed successfully.');
              if (session?.user?.id) {
                refreshAccessState(session.user.id)
              }
            }
          } else {
            console.error('[AuthContext] Backend token refresh failed:', await res.text());
            // Optional: Force logout if refresh fails? avoiding strict lockout for now
          }
        } catch (err) {
          console.error('[AuthContext] Error refreshing backend token:', err);
        }
      }

      // Automatically create profile for new users and check role mismatch
      if (event === 'SIGNED_IN' && nextUser) {
        const isSameUser = previousUserId === nextUser.id

        if (isSameUser) {
          // Same user: page refresh / session restore / tab focus.
          // getSession() already started the profile check — don't trigger another loading cycle.
          console.log('[AuthContext] SIGNED_IN for same user (refresh/restore). Skipping reload.')
          return
        }

        // Different user: actual new sign-in — show loading and verify profile
        console.log('[AuthContext] SIGNED_IN for new/different user. Triggering full profile sync.')
        setIsApproved(false)
        setIsBlocked(false)
        setAccountStatusNotice(null)
        prevIsApprovedRef.current = null
        requireProfileBeforeLoadRef.current = true
        setLoading(true)
        setIsProfileChecked(false)
        ensureUserProfile(nextUser).catch((error) => {
          console.error('[AuthContext] Error ensuring profile on sign in:', error)
        })
      }
    })

    return () => {
      subscription.unsubscribe()
      if (maxLoadingTimeoutRef.current) {
        clearTimeout(maxLoadingTimeoutRef.current)
      }
    }
  }, [refreshAccessState])

  // Effect to watch for token changes and update backend auth data
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'backend_access_token') {
        const newToken = e.newValue || ''
        const oldToken = lastTokenRef.current
        if (newToken !== oldToken) {
          console.log('[AuthContext] Backend token changed (storage event), refreshing auth info');
          lastTokenRef.current = newToken.replace(/^"+|"+$/g, '').trim()
          setTokenVersion((prev) => prev + 1) // Trigger recalculation
        }
      }
    }

    const handleBackendTokenUpdate = () => {
      const currentToken = localStorage.getItem('backend_access_token') || ''
      const normalizedToken = currentToken.replace(/^"+|"+$/g, '').trim()
      const oldToken = lastTokenRef.current
      if (normalizedToken !== oldToken) {
        console.log('[AuthContext] Backend token updated event received, refreshing auth info');
        lastTokenRef.current = normalizedToken
        setTokenVersion((prev) => prev + 1) // Trigger recalculation
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('backend-token-updated', handleBackendTokenUpdate)

    // Check for token changes periodically, but only update if token actually changed
    const tokenCheckInterval = setInterval(() => {
      const currentToken = localStorage.getItem('backend_access_token') || ''
      const normalizedToken = currentToken.replace(/^"+|"+$/g, '').trim()
      const oldToken = lastTokenRef.current

      // Only update if token actually changed
      if (normalizedToken && normalizedToken !== oldToken) {
        lastTokenRef.current = normalizedToken
        setTokenVersion((prev) => prev + 1)
      } else if (!normalizedToken && oldToken !== null) {
        // Token was removed
        lastTokenRef.current = null
        setTokenVersion((prev) => prev + 1)
      }
    }, 2000) // Check every 2 seconds instead of 1 to reduce frequency

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('backend-token-updated', handleBackendTokenUpdate)
      clearInterval(tokenCheckInterval)
    }
  }, [])

  // Track if initial session check is complete
  const [sessionChecked, setSessionChecked] = useState(false)

  // Critical: Use useEffect to set loading to false when we have valid auth data
  // This ensures loading stops as soon as we have session + role, not waiting for profile
  useEffect(() => {
    // Only set loading to false if we've checked the session
    if (!sessionChecked) return

    // Stop loading when session + role are available.
    // For fresh sign-ins (requireProfileBeforeLoadRef=true), also wait for the profile
    // check to finish so we don't flash "PendingAccess" for an approved user.
    if (session && role) {
      if (requireProfileBeforeLoadRef.current && !isProfileChecked) {
        return // Wait for profile check before showing the app
      }
      requireProfileBeforeLoadRef.current = false
      console.log('[AuthContext] Auth ready, setting loading to false')
      setLoading(false)
      if (maxLoadingTimeoutRef.current) {
        clearTimeout(maxLoadingTimeoutRef.current)
        maxLoadingTimeoutRef.current = null
      }
    } else if (session === null) {
      // If we've checked and there's no session, we're also ready (not loading)
      // This handles the case where user is not logged in
      console.log('[AuthContext] No session found, setting loading to false')
      setLoading(false)
      if (maxLoadingTimeoutRef.current) {
        clearTimeout(maxLoadingTimeoutRef.current)
        maxLoadingTimeoutRef.current = null
      }
    }
  }, [session, role, sessionChecked, isProfileChecked])

  useEffect(() => {
    const uid = session?.user?.id
    if (!uid) return
    const tick = () => refreshAccessState(uid)
    const intervalId = window.setInterval(tick, 25000)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [session?.user?.id, refreshAccessState])

  // Manage Socket connection
  useEffect(() => {
    if (user?.id) {
      const baseUrl = (window as any)._BACKEND_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';
      socketService.init(user.id, baseUrl);
    } else {
      socketService.disconnect();
    }
  }, [user]);

  const signOut = async () => {
    try {
      // Immediately clear local state for instant UI response
      setSession(null)
      setUser(null)

      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('Error signing out:', error.message)
        throw error
      }
      // Also clear backend session/token if present
      try {
        const w: any = window as any
        const baseUrl = w._BACKEND_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'
        // Optional backend logout endpoint; ignore failures to keep UX seamless
        await fetch(`${baseUrl}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => { })
      } catch (_) { }
      localStorage.removeItem('backend_access_token')
      setRole(null)
      setCenterId(null)
      setIsApproved(false)
      setIsBlocked(false)
      setAccountStatusNotice(null)
      prevIsApprovedRef.current = null
      setIsProfileChecked(false)
      // Local state was already cleared above
    } catch (error) {
      console.error('Sign out error:', error)
      // Even if signOut fails, clear local state to ensure user is logged out
      setSession(null)
      setUser(null)
      setRole(null)
      setCenterId(null)
      setIsApproved(false)
      setIsBlocked(false)
      setAccountStatusNotice(null)
      prevIsApprovedRef.current = null
      setIsProfileChecked(false)
      throw error
    }
  }

  const value = {
    session,
    user,
    loading,
    role,
    centerId,
    isApproved,
    isBlocked,
    accountStatusNotice,
    clearAccountStatusNotice,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
